import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  CobroCuotasCreditosConEmpleadosModel,
  ConstitucionDelCreditoCreditosConEmpleadosModel,
  CreditosConEmpleadosModel,
  DeudaACortoPlazoCreditosConEmpleadosModel
} from './creditos-con-empleados.model';
import { ResultModel } from '../../../shared/result.model';
import { ApiResponse } from '../../../models/base';
import { unwrapV2, V2ApiResponse } from '../../../modules_v2/shared/utils/v2-api-response.util';

const CREDITOSCONEMPLEADOS_ENDPOINT = '/creditosconempleados';
const CONSTITUCIONCREDITO_ENDPOINT = '/constituciondelcreditocreditosconempleados';
const DEUDACORTOPLAZO_ENDPOINT = '/deudasacortoplazocreditosconempleados';
const COBROCUOTAS_ENDPOINT = '/cobrocuotascreditosconempleados';
const V2_ENDPOINT = '/api/v2/employee-credits';

@Injectable({
  providedIn: 'root',
})
export class CreditosConEmpleadosService {
  constructor(private http: HttpClient) {}

  public downloadFileAttachment(uid: string): Promise<Blob> {
    let params = new HttpParams();
    params = params.append('creditosconempleadosId', uid);

    return firstValueFrom(
      this.http.get(environment.apiUrl + '/creditosconempleados/file/downloadexcel', {
        responseType: 'blob',
        params: params,
      })
    ).then((response) => {
      return new Blob([response], { type: 'application/vnd.ms-excel' });
    });
  }

  public get(uid: string): Promise<CreditosConEmpleadosModel> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<CreditosConEmpleadosModel>>(environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(uid))),
      'No se pudo cargar el crédito con empleado.'
    );
  }

  public list(offset?: number, limit?: number): Promise<ResultModel<CreditosConEmpleadosModel>> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<CreditosConEmpleadosModel[]>>(environment.apiUrl + V2_ENDPOINT)),
      'No se pudieron cargar los créditos con empleados.'
    ).then((list) => {
      const result = new ResultModel<CreditosConEmpleadosModel>();
      result.list = Array.isArray(list) ? list : [];
      result.totalItems = result.list.length;
      return result;
    });
  }

  public add(creditosConEmpleados: CreditosConEmpleadosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + CREDITOSCONEMPLEADOS_ENDPOINT, creditosConEmpleados)
    ).then((response) => response.data);
  }

  public edit(creditosConEmpleados: CreditosConEmpleadosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + CREDITOSCONEMPLEADOS_ENDPOINT, creditosConEmpleados)
    ).then((response) => response.data);
  }

  public editFileAttachment(creditosConEmpleados: CreditosConEmpleadosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(
        environment.apiUrl + CREDITOSCONEMPLEADOS_ENDPOINT + '/file',
        creditosConEmpleados
      )
    ).then((response) => response.data);
  }

  public delete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('creditosconempleadosId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + CREDITOSCONEMPLEADOS_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public constitucionDelCreditoList(
    creditosconempleadosId: string,
    plazo: number
  ): Promise<ConstitucionDelCreditoCreditosConEmpleadosModel[]> {
    const params = new HttpParams().set('plazo', plazo.toString());
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<ConstitucionDelCreditoCreditosConEmpleadosModel[]>>(
        environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(creditosconempleadosId) + '/constitutions', { params })),
      'No se pudieron cargar las constituciones.'
    ).then((list) => list || []);
  }

  public constitucionDelCreditoCreate(
    constituciondelcredito: ConstitucionDelCreditoCreditosConEmpleadosModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + CONSTITUCIONCREDITO_ENDPOINT, constituciondelcredito)
    ).then((response) => response.data);
  }

  public constitucionDelCreditoUpdate(
    constituciondelcredito: ConstitucionDelCreditoCreditosConEmpleadosModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + CONSTITUCIONCREDITO_ENDPOINT, constituciondelcredito)
    ).then((response) => response.data);
  }

  public constitucionDelCreditoDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('constituciondelcreditocreditosconempleadosId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + CONSTITUCIONCREDITO_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public deudaACortoPlazoList(
    deudaacortoplazoUid: string
  ): Promise<DeudaACortoPlazoCreditosConEmpleadosModel[]> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<DeudaACortoPlazoCreditosConEmpleadosModel[]>>(
        environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(deudaacortoplazoUid) + '/short-term-debts')),
      'No se pudieron cargar las deudas a corto plazo.'
    ).then((list) => list || []);
  }

  public deudaACortoPlazoCreate(
    deudaACortoPlazo: DeudaACortoPlazoCreditosConEmpleadosModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, deudaACortoPlazo)
    ).then((response) => response.data);
  }

  public deudaACortoPlazoUpdate(
    deudaACortoPlazo: DeudaACortoPlazoCreditosConEmpleadosModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, deudaACortoPlazo)
    ).then((response) => response.data);
  }

  public deudaACortoPlazoDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('deudasacortoplazocreditosconempleadosId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public cobroCoutasList(
    cobrocuotasUid: string
  ): Promise<CobroCuotasCreditosConEmpleadosModel[]> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<CobroCuotasCreditosConEmpleadosModel[]>>(
        environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(cobrocuotasUid) + '/installments')),
      'No se pudieron cargar las cuotas cobradas.'
    ).then((list) => list || []);
  }

  public cobroCoutasCreate(
    cobroDeCuotas: CobroCuotasCreditosConEmpleadosModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + COBROCUOTAS_ENDPOINT, cobroDeCuotas)
    ).then((response) => response.data);
  }

  public cobroCoutasUpdate(
    cobroDeCuotas: CobroCuotasCreditosConEmpleadosModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + COBROCUOTAS_ENDPOINT, cobroDeCuotas)
    ).then((response) => response.data);
  }

  public cobroCoutasDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('cobrocuotascreditosconempleadosId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + COBROCUOTAS_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }
}